import React, { useState, useRef, useEffect, useContext } from 'react';
import Tagify from '@yaireo/tagify';
import { TypeFilter } from './TypeFilter';
import { List } from './List';
import { FilterType } from '../../model/FilterType';
import { FilterItem } from '../../model/FilterItem';
import { Collapsed, getLocations, getZones } from '../../model/Collapsed';
import { AccountContext } from '../../contexts/AccountContext';
import { Filter } from '../../model/Filter';

const Filter = ({ locationId, installationId, campaignId, zone, from, to, onChange }: Props) => {
  const tagifyElementRef = useRef<HTMLInputElement | null>(null);
  const tagifyRef = useRef<Tagify | null>(null);
  const selectedItemsRef = useRef<FilterItem[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const zonesRequestRef = useRef(0);
  const zonesLocationKeyRef = useRef('');
  const initialZoneAppliedRef = useRef(false);
  const [textFilter, setTextFilter] = useState('');
  const [display, setDisplay] = useState(false);
  const [selectedItems, setSelectedItems] = useState<FilterItem[]>([]);
  const [type, setType] = useState(FilterType.all);
  const [changed, setChanged] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [installations, setInstallations] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const params = useContext(AccountContext);

  useEffect(() => {
    const tagTemplate = (tagData: any) => {
      return `<tag title="${tagData.text}" contenteditable="false" spellcheck="false" tabIndex="-1" class="tagify__tag" ${tagData.value === 'more' ? 'readonly="true' : ''}">
          <x title="" class="tagify__tag__removeBtn" role="button" aria-label="remove tag"></x>
          <div style="height: 24px; overflow: hidden;">
            <span class="tagify__tag-text">${tagData.text}</span>
          </div>
        </tag>`;
    }
    if(tagifyElementRef.current) {
      tagifyRef.current = new Tagify(tagifyElementRef.current, { 
        addTagOn: [],
        templates: {
          tag: tagTemplate
        },
        callbacks: {
          input: e => {
            const detail: any = e.detail;
            setTextFilter(detail.value);
            if(detail.value) {
              setDisplay(true);
            }      
          },
          remove: e => {
            if(e.detail.data.value !== 'more') {
              setSelectedItems(selectedItemsRef.current.filter(i => i.value !== e.detail.data.value));
              setChanged(true);
              e.detail.data.type !== FilterType.campaign && removeSelectedItems(e.detail.data.type === FilterType.location ? FilterType.installation : FilterType.campaign);
            }
          },
          focus: () => {
            setDisplay(true);
          }
        } 
      });
    }
    document.addEventListener('click', e => {
      const target: any = e.target;
      if(containerRef.current && !target.classList.contains('view-more') && !containerRef.current.contains(target)) {
        setDisplay(false);
      }
    });
  }, []);

  useEffect(() => {
    const load = async () => {
      const installations = new Map<string, any>();
      const campaigns = new Map<string, any>();
      const locations = await getLocations({ from, to }, params);
      for(let location of locations) {
        if(locationId?.includes(location.id) && !selectedItemsRef.current.some(item => item.value === location.id)) {
          onAdd({ value: location.id, type: FilterType.location, text: location.name });
        }
        for(let installation of location.installations) {
          if(!installations.has(installation.id)) {
            installations.set(installation.id, installation);
          }
          if(installationId && installationId === installation.id && !selectedItemsRef.current.some(item => item.value === installation.id)) {
            onAdd({ value: installation.id, type: FilterType.installation, text: installation.name });
          }
          for(let campaign of installation.campaigns) {
            if(!campaigns.has(campaign.id)) {
              campaigns.set(campaign.id, campaign);
            }
            if(campaignId && campaignId === campaign.id && !selectedItemsRef.current.some(item => item.value === campaign.id)) {
              onAdd({ value: campaign.id, type: FilterType.campaign, text: campaign.name });
            }
          }
        }
      }
      setChanged(false);
      setLocations(locations);
      setInstallations(Array.from(installations.values()));
      setCampaigns(Array.from(campaigns.values()));
    };
    load();
  }, [ from, to ]);

  useEffect(() => {
    selectedItemsRef.current = selectedItems;
    const locationIds = selectedItems
      .filter(item => item.type === FilterType.location)
      .map(item => item.value);
    if(!locationIds.length && type === FilterType.zone) {
      setType(FilterType.all);
    }
    if(!locationIds.length) {
      zonesLocationKeyRef.current = '';
      zonesRequestRef.current++;
      setZones([]);
      removeSelectedItems(FilterType.zone);
      return;
    }
    const locationKey = locationIds.join('|');
    if(zonesLocationKeyRef.current === locationKey) {
      return;
    }
    zonesLocationKeyRef.current = locationKey;
    setZones([]);
    const requestId = ++zonesRequestRef.current;
    const loadZones = async () => {
      try {
        const zones = await getZones({ locationId: locationIds, from: from, to: to }, params);
        if(requestId !== zonesRequestRef.current) {
          return;
        }
        setZones(zones);
        const availableZones = zones.map(zone => zone.id);
        for(let selectedZone of selectedItemsRef.current.filter(item => item.type === FilterType.zone)) {
          if(!availableZones.includes(selectedZone.value)) {
            onRemove(selectedZone);
          }
        }
        if(zone && !initialZoneAppliedRef.current) {
          initialZoneAppliedRef.current = true;
          const initialZone = zones.find(z => z.name === zone);
          if(initialZone && !selectedItemsRef.current.some(item => item.type === FilterType.zone && item.value === zone)) {
            onAdd({ value: initialZone.id, type: FilterType.zone, text: initialZone.name });
          }
        }
      }
      catch(err) {
        console.error('Error loading zones', err);
        if(requestId === zonesRequestRef.current) {
          setZones([]);
        }
      }
    };
    loadZones();
  }, [ selectedItems ]);

  useEffect(() => {
    const tags = tagifyRef.current;
    if(tags) {
      const tagElements = tags.getTagElms();
      if(!display) {
        const inputWidth = 50;
        let widthAvailable = tags.DOM.scope.clientWidth - inputWidth;
        let showEllipsis = false;
        for(let tag of tagElements) {
          widthAvailable -= tag.clientWidth;
          if(widthAvailable <= 0) {
            tag.style.display = 'none';
            showEllipsis = true;
          }
        }
        if(showEllipsis) {
          tags.addTags([{ value: 'more', text: '...' }]);
        }
      }
      else {
        tags.removeTags('more');
        for(let tag of tagElements) {
          tag.style.display = 'flex';
        }
      }
    }
  }, [ display ]);

  const onToggle = () => {
    setDisplay(!display);
  }

  const onChangeType = (type: FilterType) => {
    setType(type);
  }

  const onAdd = (item: FilterItem) => {
    tagifyRef.current && tagifyRef.current.addTags([ { ...item, editable: false } ]);
    selectedItems.push(item);
    setSelectedItems([ ...selectedItems ]);
    setChanged(true);
  }

  const onRemove = (item: FilterItem) => {
    tagifyRef.current && tagifyRef.current.removeTags(item.value);
  }

  const showZones = selectedItems.some(item => item.type === FilterType.location);

  const removeSelectedItems = (type: FilterType) => {
    const itemsToRemove = selectedItemsRef.current.filter(i => i.type === type);
    for(let item of itemsToRemove) {
      onRemove(item);
    }
  }

  const onApply = () => {
    const locations = getSelectedMany(FilterType.location);
    const installation = getSelected(FilterType.installation);
    const campaign = getSelected(FilterType.campaign);
    const zone = getSelected(FilterType.zone);
    const locationIds = locations.map(l => l.id);
    const locationNames = locations.map(l => l.name);
    onChange(locationIds.length ? locationIds : undefined, locationNames.length ? locationNames : undefined, installation.id, installation.name, campaign.id, campaign.name, zone.id);
    setDisplay(false);
    setChanged(false);
  }

  const getSelected = (type: FilterType): { id: string, name: string } => {
    for(let s of selectedItemsRef.current) {
      if(s.type === type) {
        return { id: s.value, name: s.text };
      }
    }
    return { id: '', name: '' };
  }

  const getSelectedMany = (type: FilterType): { id: string, name: string }[] => {
    return selectedItemsRef.current
      .filter(s => s.type === type)
      .map(s => ({ id: s.value, name: s.text }));
  }

  return (
    <div ref={containerRef} className="d-flex align-items-center ps-2 pe-2">
      <div className="flex-grow-1">
        <div className="position-relative z-2">
          <div className="position-absolute w-100" style={{ marginTop: -18.85, height: 37.6 }}>
            <div className="d-flex w-100">
              <div className={`input-group input-group-merge ${display ? 'tags-expanded' : 'tags-collapsed'}`}>
                <span className="input-group-text">
                  <i className="ti ti-search" />
                </span>
                <input ref={tagifyElementRef} className="form-control flex-grow-1 d-flex" />
                <span className="input-group-text" onClick={onToggle}>
                  <i className="ti ti-chevron-down" />
                </span>
              </div>
              <button type="button" className={`btn btn-primary waves-effect ms-1 ${!changed ? 'd-none' : '' }`} style={{ height: 37.6 }} onClick={onApply}>Apply</button>
              <button type="button" className={`btn btn-label-secondary ms-1 ps-2 pe-2 ${changed || !selectedItemsRef.current.length ? 'd-none' : '' }`} style={{ height: 37.6 }} onClick={onToggle}>
                <i className="ti ti-circle-plus"></i>
              </button>
            </div>
            <div className={`card card-body w-100 ${!display ? 'd-none' : ''}`}>
              <div className="d-flex">
                <TypeFilter type={type} showZones={showZones} onChange={onChangeType} />
              </div>
              <List textFilter={textFilter} type={type} locations={locations} installations={installations} campaigns={campaigns} zones={zones} selected={selectedItems} showZones={showZones} onAdd={onAdd} onRemove={onRemove} onChangeType={onChangeType} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  locationId?: string[],
  installationId?: string,
  campaignId?: string,
  zone?: string,
  from?: Date,
  to?: Date,
  onChange: (locationId?: string[], locationName?: string[], installationId?: string, installationName?: string, campaignId?: string, campaignName?: string, zone?: string) => void
}

export { Filter };
